import React, { useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, LinearProgress, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Typography, Snackbar,
} from '@mui/material';
import { getApiBaseUrl, fetcherRilis, usePenggunaAplikasi, DaftarPenggunaAplikasi } from './PenggunaAplikasi';

const mb = (b) => (b ? `${(b / 1024 / 1024).toFixed(1)} MB` : '-');
const tgl = (iso) => new Date(iso).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const FORM_KOSONG = { versi: '', build: '', catatanPerbaikan: '', catatanTambahan: '', wajib: false, apk64: null, apk32: null };

/** Superadmin: unggah & kelola rilis aplikasi Flutter (APK), plus akun yang memakai aplikasi per cabang. */
export const RilisAplikasi = () => {
  const base = getApiBaseUrl();
  const { data, error, mutate } = useSWR(`${base}/aplikasi/rilis`, fetcherRilis);
  const pengguna = usePenggunaAplikasi();
  const [form, setForm] = useState(null);
  const [ubah, setUbah] = useState(null);
  const [progres, setProgres] = useState(null);
  const [galat, setGalat] = useState('');
  const [pesan, setPesan] = useState('');

  const bukaBaru = () => {
    setGalat('');
    setForm({ ...FORM_KOSONG });
  };

  const unggah = async () => {
    if (!form.apk64) {
      setGalat('APK 64-bit wajib dipilih.');
      return;
    }
    const fd = new FormData();
    ['versi', 'build', 'catatanPerbaikan', 'catatanTambahan'].forEach((k) => fd.append(k, form[k]));
    fd.append('wajib', form.wajib ? 'true' : 'false');
    fd.append('apk64', form.apk64);
    if (form.apk32) fd.append('apk32', form.apk32);
    setGalat('');
    setProgres(0);
    try {
      const r = await axios.post(`${base}/aplikasi/rilis`, fd, {
        withCredentials: true,
        onUploadProgress: (e) => e.total && setProgres(Math.round((e.loaded / e.total) * 100)),
      });
      setForm(null);
      setPesan(r.data?.message || 'Rilis diterbitkan');
      mutate();
    } catch (e) {
      setGalat(e.response?.data?.message || e.message);
    } finally {
      setProgres(null);
    }
  };

  const simpanUbah = async () => {
    const fd = new FormData();
    fd.append('catatanPerbaikan', ubah.catatanPerbaikan);
    fd.append('catatanTambahan', ubah.catatanTambahan);
    fd.append('wajib', ubah.wajib ? 'true' : 'false');
    if (ubah.apk32) fd.append('apk32', ubah.apk32);
    setGalat('');
    setProgres(ubah.apk32 ? 0 : null);
    try {
      await axios.put(`${base}/aplikasi/rilis/${ubah.uuid}`, fd, {
        withCredentials: true,
        onUploadProgress: (e) => ubah.apk32 && e.total && setProgres(Math.round((e.loaded / e.total) * 100)),
      });
      setUbah(null);
      setPesan('Rilis diperbarui');
      mutate();
    } catch (e) {
      setGalat(e.response?.data?.message || e.message);
    } finally {
      setProgres(null);
    }
  };

  const ubahStatus = async (r) => {
    const tarik = r.status === 'terbit';
    const ok = await Swal.fire({
      title: tarik ? `Tarik rilis ${r.versi}?` : `Terbitkan lagi rilis ${r.versi}?`,
      text: tarik ? 'Aplikasi tidak lagi ditawari versi ini; tautan unduhannya berhenti berlaku.' : 'Versi ini kembali ditawarkan bila menjadi yang terbaru.',
      icon: 'question', showCancelButton: true, confirmButtonText: tarik ? 'Tarik' : 'Terbitkan', cancelButtonText: 'Batal',
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.put(`${base}/aplikasi/rilis/${r.uuid}`, { status: tarik ? 'ditarik' : 'terbit' }, { withCredentials: true });
      setPesan(tarik ? 'Rilis ditarik' : 'Rilis diterbitkan lagi');
    } catch (e) {
      Swal.fire('Gagal', e.response?.data?.message || e.message, 'error');
    }
    mutate();
  };

  const hapus = async (r) => {
    const ok = await Swal.fire({
      title: `Hapus rilis ${r.versi}?`, text: 'Berkas APK-nya ikut dihapus dari server.', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Hapus', cancelButtonText: 'Batal', confirmButtonColor: '#d33',
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${base}/aplikasi/rilis/${r.uuid}`, { withCredentials: true });
      setPesan('Rilis dihapus');
    } catch (e) {
      Swal.fire('Gagal', e.response?.data?.message || e.message, 'error');
    }
    mutate();
  };

  const rilis = data?.data || [];
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Rilis Aplikasi</Typography>
          <Typography variant="body2" color="text.secondary">
            Unggah APK aplikasi Juragan POS. Aplikasi di HP menawarkan update otomatis bila ada rilis dengan nomor build lebih besar.
          </Typography>
        </Box>
        <Button variant="contained" onClick={bukaBaru}>Rilis baru</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.response?.data?.message || error.message}</Alert>}

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardHeader title="Riwayat rilis" subheader="Terbaru di atas. Tarik rilis yang bermasalah supaya tidak ditawarkan lagi." />
        <CardContent sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Versi</TableCell>
                <TableCell>Catatan</TableCell>
                <TableCell>APK</TableCell>
                <TableCell>Dipakai</TableCell>
                <TableCell align="right">Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data && !rilis.length && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.disabled', py: 4 }}>Belum ada rilis. Klik "Rilis baru".</TableCell></TableRow>
              )}
              {rilis.map((r) => (
                <TableRow key={r.uuid} sx={{ opacity: r.status === 'ditarik' ? 0.55 : 1, verticalAlign: 'top' }}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography fontWeight={700}>{r.versi}</Typography>
                    <Typography variant="caption" color="text.secondary" component="div">build {r.build} · {tgl(r.createdAt)}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {r.build === data.terbaru && <Chip size="small" color="primary" label="Terbaru" />}
                      {r.status === 'ditarik' && <Chip size="small" label="Ditarik" />}
                      {r.wajib && <Chip size="small" color="warning" label="Wajib" />}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 220 }}>
                    {r.catatanTambahan && <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}><b>Tambahan:</b>{'\n'}{r.catatanTambahan}</Typography>}
                    {r.catatanPerbaikan && <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}><b>Perbaikan:</b>{'\n'}{r.catatanPerbaikan}</Typography>}
                    {!r.catatanTambahan && !r.catatanPerbaikan && <Typography variant="body2" color="text.disabled">-</Typography>}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2">64-bit: {r.ada64 ? mb(r.ukuran64) : '-'}</Typography>
                    <Typography variant="body2" color={r.ada32 ? 'text.primary' : 'text.disabled'}>32-bit: {r.ada32 ? mb(r.ukuran32) : 'belum ada'}</Typography>
                  </TableCell>
                  <TableCell>{r.jumlahPengguna} akun</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" onClick={() => { setGalat(''); setUbah({ ...r, apk32: null }); }}>Ubah</Button>
                    <Button size="small" onClick={() => ubahStatus(r)}>{r.status === 'terbit' ? 'Tarik' : 'Terbitkan'}</Button>
                    <Button size="small" color="error" onClick={() => hapus(r)}>Hapus</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 2 }}>
        <CardHeader title="Pengguna aplikasi" subheader="Akun yang memakai aplikasi per cabang beserta versinya. Akun yang hanya memakai web tidak tampil." />
        <CardContent sx={{ overflowX: 'auto' }}>
          <DaftarPenggunaAplikasi {...pengguna} />
        </CardContent>
      </Card>

      <Dialog open={!!form} onClose={() => progres === null && setForm(null)} fullWidth maxWidth="sm">
        <DialogTitle>Rilis baru</DialogTitle>
        {form && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
            {galat && <Alert severity="error">{galat}</Alert>}
            <Alert severity="info">
              Sebelum build: naikkan versi dengan <code>dart run tool/naikkan_versi.dart</code> (atau <code>minor</code>),
              lalu <code>flutter build apk --release --split-per-abi</code>. Versi & nomor build dibaca otomatis dari APK
              {data?.buildBerikutnya ? ` (build harus ${data.buildBerikutnya} atau lebih)` : ''}.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={7}><TextField fullWidth size="small" label="Versi" placeholder="otomatis dari APK" InputLabelProps={{ shrink: true }} value={form.versi} onChange={(e) => setForm({ ...form, versi: e.target.value })} /></Grid>
              <Grid item xs={5}><TextField fullWidth size="small" label="Nomor build" placeholder="otomatis" InputLabelProps={{ shrink: true }} type="number" value={form.build} onChange={(e) => setForm({ ...form, build: e.target.value })} /></Grid>
            </Grid>
            <TextField label="Tambahan (satu per baris)" multiline minRows={3} size="small" value={form.catatanTambahan} onChange={(e) => setForm({ ...form, catatanTambahan: e.target.value })} />
            <TextField label="Perbaikan (satu per baris)" multiline minRows={3} size="small" value={form.catatanPerbaikan} onChange={(e) => setForm({ ...form, catatanPerbaikan: e.target.value })} />
            <FormControlLabel
              control={<Switch checked={form.wajib} onChange={(e) => setForm({ ...form, wajib: e.target.checked })} />}
              label="Wajib update (versi lebih lama harus update sebelum bisa dipakai saat online)"
            />
            <Box>
              <Button variant="outlined" component="label" size="small">
                APK 64-bit * <input hidden type="file" accept=".apk" onChange={(e) => setForm({ ...form, apk64: e.target.files[0] || null })} />
              </Button>
              <Typography variant="caption" sx={{ ml: 1 }}>{form.apk64 ? `${form.apk64.name} (${mb(form.apk64.size)})` : 'app-arm64-v8a-release.apk'}</Typography>
            </Box>
            <Box>
              <Button variant="outlined" component="label" size="small">
                APK 32-bit <input hidden type="file" accept=".apk" onChange={(e) => setForm({ ...form, apk32: e.target.files[0] || null })} />
              </Button>
              <Typography variant="caption" sx={{ ml: 1 }}>{form.apk32 ? `${form.apk32.name} (${mb(form.apk32.size)})` : 'app-armeabi-v7a-release.apk (untuk HP lama)'}</Typography>
            </Box>
            {progres !== null && <Box><LinearProgress variant="determinate" value={progres} /><Typography variant="caption">Mengunggah {progres}%</Typography></Box>}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setForm(null)} disabled={progres !== null}>Batal</Button>
          <Button variant="contained" onClick={unggah} disabled={progres !== null}>Terbitkan</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!ubah} onClose={() => progres === null && setUbah(null)} fullWidth maxWidth="sm">
        <DialogTitle>Ubah rilis {ubah?.versi}</DialogTitle>
        {ubah && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
            {galat && <Alert severity="error">{galat}</Alert>}
            <TextField label="Tambahan (satu per baris)" multiline minRows={3} size="small" value={ubah.catatanTambahan} onChange={(e) => setUbah({ ...ubah, catatanTambahan: e.target.value })} />
            <TextField label="Perbaikan (satu per baris)" multiline minRows={3} size="small" value={ubah.catatanPerbaikan} onChange={(e) => setUbah({ ...ubah, catatanPerbaikan: e.target.value })} />
            <FormControlLabel control={<Switch checked={ubah.wajib} onChange={(e) => setUbah({ ...ubah, wajib: e.target.checked })} />} label="Wajib update" />
            <Box>
              <Button variant="outlined" component="label" size="small">
                {ubah.ada32 ? 'Ganti APK 32-bit' : 'Tambah APK 32-bit'}
                <input hidden type="file" accept=".apk" onChange={(e) => setUbah({ ...ubah, apk32: e.target.files[0] || null })} />
              </Button>
              <Typography variant="caption" sx={{ ml: 1 }}>{ubah.apk32 ? ubah.apk32.name : ''}</Typography>
            </Box>
            {progres !== null && <LinearProgress variant="determinate" value={progres} />}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setUbah(null)} disabled={progres !== null}>Batal</Button>
          <Button variant="contained" onClick={simpanUbah} disabled={progres !== null}>Simpan</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!pesan} autoHideDuration={2500} onClose={() => setPesan('')} message={pesan} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};
